import { zodResolver } from "@hookform/resolvers/zod";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import LoadingButton from "@mui/lab/LoadingButton";
import { Box, Button, FormControl, Grid, Typography } from "@mui/material";
import { useState } from "react";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { ControlledSelect } from "../../../components/form/ControlledSelect/ControlledSelect";
import { ControlledTextField } from "../../../components/form/ControlledTextField/ControlledTextField";

const createOfferTemplateFormSchema = (t) =>
    z.object({
        templateNumber: z.string().min(1, t("drafts.validation.templateNumberRequired")),
        templateLanguage: z.string().min(1, t("drafts.validation.languageRequired")),
        templateName: z.string().min(1, t("drafts.validation.templateNameRequired")),
        templateDescription: z.string().min(1, t("drafts.validation.descriptionRequired")),
        active: z.string().optional(),
    });

export function DocxDraftForm(props) {
    const { t } = useTranslation();
    const [uploadedFile, setUploadedFile] = useState(null);
    const offerTemplateFormSchema = createOfferTemplateFormSchema(t);

    const { handleSubmit, control, watch } = useForm({
        resolver: zodResolver(offerTemplateFormSchema),
        defaultValues: {
            templateNumber: "",
            templateLanguage: "",
            templateName: "",
            templateDescription: "",
            active: "",
            ...props.defaultValues,
        },
    });

    const watchFields = [
        "templateNumber",
        "templateLanguage",
        "templateName",
        "templateDescription",
    ];
    if (props.showActiveField) {
        watchFields.push("active");
    }
    // @ts-ignore
    let submitEnabled = watch(watchFields).reduce((acc, curr) => acc && Boolean(curr), true);
    if (props.showFileUpload) {
        submitEnabled = submitEnabled && Boolean(uploadedFile);
    }

    const languageOptions = [
        { value: "PL", label: t("drafts.languages.PL") },
        { value: "EN", label: t("drafts.languages.EN") },
    ];

    const activeOptions = [
        { value: "true", label: t("yes") },
        { value: "false", label: t("no") },
    ];

    const validateFileType = (file) => {
        const allowedExtensions = [".docx"];
        const fileName = file.name.toLowerCase();
        const fileExtension = fileName.substring(fileName.lastIndexOf("."));

        return allowedExtensions.includes(fileExtension);
    };

    const handleFileUpload = (event) => {
        const file = event.target.files?.[0];
        if (file) {
            if (!validateFileType(file)) {
                toast.error(t("drafts.validation.onlyDocxAllowed"));
                event.target.value = "";
                return;
            }
            setUploadedFile(file);
        }
    };

    const handleFormSubmit = (data) => {
        props.onSubmit({ ...data, file: uploadedFile || undefined });
    };

    return (
        <Box sx={{ p: 3, pt: 4 }}>
            <Grid container direction={"column"} spacing={3}>
                <FormControl>
                    <Grid container spacing={2}>
                        <Grid item xs={6}>
                            <ControlledTextField
                                control={control}
                                name="templateNumber"
                                label={`${t("drafts.columns.templateNumber")}*`}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <ControlledSelect
                                control={control}
                                name="templateLanguage"
                                label={`${t("drafts.columns.language")}*`}
                                data={languageOptions}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <ControlledTextField
                                control={control}
                                name="templateName"
                                label={`${t("drafts.templateName")}*`}
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <ControlledTextField
                                control={control}
                                name="templateDescription"
                                label={`${t("drafts.description")}*`}
                                props={{
                                    InputProps: {
                                        rows: 4,
                                        multiline: true,
                                        inputComponent: "textarea",
                                    },
                                }}
                            />
                        </Grid>

                        {props.showActiveField && (
                            <Grid item xs={12}>
                                <ControlledSelect
                                    control={control}
                                    name="active"
                                    label={`${t("drafts.columns.active")}*`}
                                    data={activeOptions}
                                />
                            </Grid>
                        )}

                        {props.showFileUpload && (
                            <Grid item xs={12}>
                                <Box
                                    sx={{
                                        display: "flex",
                                        justifyContent: "flex-end",
                                        alignItems: "center",
                                        flexDirection: "column",
                                        gap: 2,
                                        maxWidth: "300px",
                                        ml: "auto",
                                    }}
                                >
                                    <Button
                                        component="label"
                                        variant="outlined"
                                        startIcon={<CloudUploadIcon />}
                                    >
                                        {t("drafts.offerTemplates.addTemplate")}
                                        <input
                                            type="file"
                                            hidden
                                            accept=".docx"
                                            onChange={handleFileUpload}
                                        />
                                    </Button>

                                    {uploadedFile ? (
                                        <Typography variant="body2" color="primary">
                                            {uploadedFile.name}
                                        </Typography>
                                    ) : (
                                        <Typography variant="body2" color="text.secondary">
                                            {t("drafts.offerTemplates.noTemplate")}
                                        </Typography>
                                    )}
                                </Box>
                            </Grid>
                        )}
                    </Grid>

                    <Grid item xs={12} sx={{ mt: 4 }}>
                        <LoadingButton
                            type="submit"
                            variant="contained"
                            color="success"
                            fullWidth
                            disabled={!submitEnabled}
                            onClick={handleSubmit(handleFormSubmit, props.onError)}
                            sx={{
                                backgroundColor: "#4caf50",
                                "&:hover": {
                                    backgroundColor: "#45a049",
                                },
                            }}
                        >
                            {props.submitBtnText}
                        </LoadingButton>
                    </Grid>
                </FormControl>
            </Grid>
        </Box>
    );
}
